import { Head, Link, usePage } from '@inertiajs/react'
import { useEffect, useMemo, useState } from 'react'
import { TaxCategoryType } from './types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import Form from './form'

interface IndexProps {
  tax_categories: TaxCategoryType[]
}

export default function Index({ tax_categories }: IndexProps) {
  const { flash } = usePage()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] =
    useState<TaxCategoryType | null>(null)

  const emptyCategory = useMemo(
    () => ({
      id: 0,
      name: '',
      code: '',
      status: 'active',
    }),
    []
  )

  const isEditing = Boolean(editingCategory)
  const formCategory = editingCategory ?? emptyCategory
  const formAction = isEditing
    ? `/tax_categories/${formCategory.id}`
    : '/tax_categories'
  const formMethod = isEditing ? 'patch' : 'post'
  const formTitle = isEditing ? 'Edit tax category' : 'New tax category'
  const formSubmitText = isEditing ? 'Update Tax Category' : 'Create Tax Category'

  const openCreateDialog = () => {
    setEditingCategory(null)
    setDialogOpen(true)
  }

  const openEditDialog = (taxCategory: TaxCategoryType) => {
    setEditingCategory(taxCategory)
    setDialogOpen(true)
  }

  useEffect(() => {
    if (flash?.notice) {
      setDialogOpen(false)
      setEditingCategory(null)
    }
  }, [flash?.notice])

  return (
    <>
      <Head title="Tax categories" />
      <div className="mx-auto md:w-2/3 w-full px-8 pt-8">
        {flash.notice && (
          <p className="py-2 px-3 bg-green-50 mb-5 text-green-500 font-medium rounded-lg inline-block">
            {flash.notice}
          </p>
        )}
        <div className="flex justify-between items-center">
          <h1 className="font-bold text-4xl">Tax categories</h1>
          <Button onClick={openCreateDialog}>New tax category</Button>
        </div>

        <div className="min-w-full mt-6 overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b">
                <th className="py-3 pr-4 font-semibold">Name</th>
                <th className="py-3 pr-4 font-semibold">Code</th>
                <th className="py-3 pr-4 font-semibold">Status</th>
                <th className="py-3 pr-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tax_categories.map((tax_category) => (
                <tr key={tax_category.id} className="border-b last:border-b-0">
                  <td className="py-3 pr-4">{tax_category.name}</td>
                  <td className="py-3 pr-4">{tax_category.code}</td>
                  <td className="py-3 pr-4">{tax_category.status}</td>
                  <td className="py-3 pr-4">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(tax_category)}
                      >
                        Edit
                      </Button>
                      <Link
                        href={`/tax_categories/${tax_category.id}`}
                        className="rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-accent"
                      >
                        View
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
              {tax_categories.length === 0 && (
                <tr>
                  <td className="py-6 text-sm text-muted-foreground" colSpan={4}>
                    No tax categories found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{formTitle}</DialogTitle>
          </DialogHeader>
          <Form
            key={`${isEditing ? formCategory.id : 'new'}`}
            tax_category={formCategory}
            action={formAction}
            method={formMethod}
            submitText={formSubmitText}
            onSuccess={() => {
              setDialogOpen(false)
              setEditingCategory(null)
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
